import React, { useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Field,
  Input,
  Textarea,
} from '@billda/design-system';
import { FormProvider, useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useUpdateSite } from '../../../hooks/useUpdateSite';
import {
  editSiteSchema,
  normalizeWhat3WordsAddress,
  type EditSiteFormData,
} from '../../../validation/editSiteSchema';
import What3WordsField from '../../import/steps/SiteInformationStep/What3WordsField';

export interface EditSiteDialogProps {
  open: boolean;
  onClose: () => void;
  site: {
    id: number;
    name: string;
    what3WordsAddress: string | null;
    notes: string | null;
    latitude?: number | null;
    longitude?: number | null;
  };
}

/**
 * Dialog for editing a site's name, What3Words address and notes.
 * The slug is not editable and is omitted entirely.
 */
export const EditSiteDialog: React.FC<EditSiteDialogProps> = ({
  open,
  onClose,
  site,
}) => {
  const mutation = useUpdateSite(site.id);

  const defaultValues: EditSiteFormData = {
    name: site.name,
    what3words: normalizeWhat3WordsAddress(site.what3WordsAddress ?? ''),
    notes: site.notes ?? '',
    latitude: site.latitude ?? null,
    longitude: site.longitude ?? null,
  };

  const form = useForm<EditSiteFormData>({
    resolver: yupResolver(editSiteSchema),
    defaultValues,
    mode: 'onBlur',
  });

  const { control, handleSubmit, reset } = form;

  // Re-seed defaults each time the dialog opens so stale edits are discarded
  useEffect(() => {
    if (open) {
      reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, site, reset]);

  const onSubmit = (data: EditSiteFormData) => {
    mutation.mutate(
      {
        name: data.name.trim(),
        what3WordsAddress: data.what3words?.trim() ?? '',
        notes: data.notes?.trim() ? data.notes.trim() : null,
      },
      { onSuccess: () => onClose() }
    );
  };

  return (
    <Dialog open={open} onOpenChange={nextOpen => !nextOpen && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>Edit site</DialogHeader>
        <DialogBody className="sr-only">Edit site details.</DialogBody>
        <FormProvider {...form}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="space-y-4 pt-2">
              <Controller
                name="name"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Field label="Name" required error={error?.message}>
                    <Input
                      {...field}
                      autoFocus
                      maxLength={100}
                      data-testid="site-edit-name"
                    />
                  </Field>
                )}
              />
              <What3WordsField testId="site-edit-what3words" />
              <Controller
                name="notes"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Field label="Notes" error={error?.message}>
                    <Textarea
                      {...field}
                      value={field.value ?? ''}
                      rows={3}
                      maxLength={4048}
                      data-testid="site-edit-notes"
                    />
                  </Field>
                )}
              />
            </div>
            <DialogFooter>
              <Button
                variant="ghost"
                onClick={onClose}
                data-testid="site-edit-cancel"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                loading={mutation.isPending}
                disabled={mutation.isPending}
                data-testid="site-edit-save"
              >
                Save
              </Button>
            </DialogFooter>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
};
